/* eslint-disable no-undef */
/**
 * @fileOverview This file contains the axiosClient utility function and related constants for making HTTP requests.
 * @module axiosClient
 */

import axios from "axios";
import { encryptAndStoreToken, getDecryptedToken } from "./CryptoJS";
import LocalstorageService from "./localstorage-services";
// import Cookies from "universal-cookie";
import { openToast } from "store/slices/toast.slice";
import { getApiMessage } from "./apiErrorCode";
import configStore from "store";
import { AuthSwitchAccountNewAction } from "store/middlewares/session/auth";
import { logout } from "store/slices/session/auth";
import { urls } from "config/urls";
import { attachEncryption } from "./encryption/manager";

axios.defaults.withCredentials = false;
let apiURL = "";
let accountURL = "";

/**
 * Configuration options for the you only environment.
 * @type {Object}
 */
const bigfoot = {
  name: "bigfoot",
  api: urls.API_URL,
  live: urls.DRIVE_URL,
};

/**
 * The domain of the application. If you're running the application locally, make sure to add the domain to the .env file.
 * @type {string}
 */
export const domain = process.env.REACT_APP_DOMAIN;

/**
 * Configuration options for the localhost environment.
 * @type {Object}
 */
const localhost = {
  live: "http://localhost:3000",
  api: urls.API_URL,
};

const path = window.location.href;
const live = !!path.match(bigfoot.live);

if (live) {
  accountURL = bigfoot.live;
  apiURL = bigfoot.api;
} else {
  accountURL = localhost.live;
  apiURL = localhost.api;
}

export const API_URL = apiURL;
export const ACCOUNT_URL = accountURL;

const getCurrentUser = () =>
  JSON.parse(localStorage.getItem("bigfoot-drive"))?.user || {};
// const getOtherInfo = () =>
//   JSON.parse(localStorage.getItem("bigfoot-otherInfo")) || {};

const handleUnauthorizedOrForbidden = async () => {
  const currentUser = getCurrentUser();
  let tempObj = { refreshToken: getDecryptedToken(currentUser?.refreshToken) };
  try {
    const response = await attachEncryption(axios.create()).post(
      `${API_URL}auth/refreshToken`,
      tempObj,
    );
    const user = {
      ...currentUser,
      token: encryptAndStoreToken(response.data.response.accessToken),
    };
    LocalstorageService.loginUser(user);
    // Reload the page or retry the request after token update
    window.location.reload();
  } catch (error) {
    const store = configStore();
    LocalstorageService.removeEmailFromOtherInfo(currentUser?.email);
    LocalstorageService.removeFromLocalStorage("bigfoot-drive");
    store.dispatch(logout());
    if (
      currentUser?.accountsOnDevice?.some(
        (account) => account?.is_accessible === true,
      ) &&
      currentUser?.userSession?.device_id
    ) {
      store.dispatch(
        AuthSwitchAccountNewAction({
          deviceId: currentUser?.userSession?.device_id,
          appType: "drive",
        }),
      );
    } else {
      store.dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      const redirectUrl = `${urls.DRIVE_URL}drive/${currentUser?.drive_start_page}`;
      const accountLoginUrl = `${urls.ACCOUNT_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
      window.location.href = accountLoginUrl;
    }

    // store.dispatch(
    //   openToast({
    //     type: "error",
    //     message: error?.response?.data?.statusCode
    //       ? getApiMessage(error?.response?.data?.statusCode)
    //       : error?.message,
    //   }),
    // );
    // LocalstorageService.removeEmailFromOtherInfo(existingUser?.email);
    // LocalstorageService.removeFromLocalStorage("bigfoot-drive");
    // setTimeout(() => {
    //   window.location.href = "/";
    // }, 2000);
  }
};

/**
 * The axios client function for making HTTP requests to api.
 * @function axiosClient
 */
// export function axiosClient() {
//   // gets the email and refresh cookies
//   const currentUser = getCurrentUser();
//   const webToken = currentUser?.token;
//   const store = configStore();

//   const defaultOptions = {
//     baseURL: apiURL,
//     headers: {
//       "Content-Type": "application/json;charset=UTF-8",
//       // "Access-Control-Allow-Credentials": true,
//       accept: "application/json",
//     },
//   };

//   const instance = axios.create(defaultOptions);
//   console.log("currentUser", currentUser);

//   // Set the AUTH token for any request
//   if (webToken) {
//     instance.interceptors.request.use(
//       (config) => {
//         if (webToken) {
//           config.headers.Authorization = `Bearer ${getDecryptedToken(
//             webToken,
//           )}`;
//         }
//         return config;
//       },
//       (error) => Promise.reject(error),
//     );

//     instance.defaults.headers.common["Authorization"] =
//       `Bearer ${getDecryptedToken(webToken)}`;

//     instance.interceptors.response.use(
//       (response) => response,
//       async (error) => {
//         const status = error.response?.status;

//         if (status === 401) {
//           await handleUnauthorizedOrForbidden();
//         }

//         // if (status === 403) {
//         //   LocalstorageService.removeEmailFromOtherInfo(existingUser?.email);
//         //   LocalstorageService.removeFromLocalStorage("bigfoot-drive");
//         //   setTimeout(() => {
//         //     window.location.href = "/";
//         //   }, 1000);
//         // }

//         if (status === 403) {
//           const currentUser = getCurrentUser();
//           const otherInfo = getOtherInfo();
//           const remainingEmails = LocalstorageService.removeEmailFromOtherInfo(
//             currentUser?.driveEmail,
//           );
//           LocalstorageService.removeFromLocalStorage("bigfoot-drive");

//           if (remainingEmails?.length > 0 && otherInfo?.deviceId) {
//             store.dispatch(
//               AuthSwitchAccountNewAction({
//                 deviceId: otherInfo?.deviceId,
//                 appType: "mail",
//               }),
//             );
//           } else {
//             const redirectUrl = window.location.href;
//             const accountLoginUrl = `${urls.ACCOUNT_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
//             window.location.href = accountLoginUrl;
//           }
//         }

//         return Promise.reject(error);
//       },
//     );
//   }

//   return instance;
// }

export function axiosClient() {
  const currentUser = getCurrentUser();
  const webToken = currentUser?.token;
  const store = configStore();

  const defaultOptions = {
    baseURL: apiURL,
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      accept: "application/json",
      "X-Device-Id": currentUser?.userSession?.device_id,
    },
  };

  const instance = axios.create(defaultOptions);
  attachEncryption(instance);

  // Request interceptor
  if (webToken) {
    instance.interceptors.request.use(
      (config) => {
        config.headers.Authorization = `Bearer ${getDecryptedToken(webToken)}`;
        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.defaults.headers.common.Authorization = `Bearer ${getDecryptedToken(webToken)}`;
  }

  // Response interceptor - ALWAYS register
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status;
      const currentUser = getCurrentUser();

      // 401
      if (status === 401) {
        // No refresh token -> Login page
        if (!currentUser?.refreshToken) {
          const redirectUrl = `${urls.DRIVE_URL}drive/${currentUser?.drive_start_page}`;
          const accountLoginUrl = `${urls.ACCOUNT_URL}?redirect=${encodeURIComponent(
            redirectUrl,
          )}`;

          window.location.href = accountLoginUrl;
          return Promise.reject(error);
        }

        // Refresh token available
        await handleUnauthorizedOrForbidden();
      }

      // 403
      if (status === 403) {
        // No token -> Login page
        // if (!currentUser?.refreshToken) {
        //   const redirectUrl = window.location.href;
        //   const accountLoginUrl = `${urls.ACCOUNT_URL}?redirect=${encodeURIComponent(
        //     redirectUrl,
        //   )}`;

        //   window.location.href = accountLoginUrl;
        //   return Promise.reject(error);
        // }

        // const otherInfo = getOtherInfo();

        LocalstorageService.removeEmailFromOtherInfo(currentUser?.email);
        store.dispatch(logout());

        if (
          currentUser?.accountsOnDevice?.some(
            (account) => account?.is_accessible === true,
          ) &&
          currentUser?.userSession?.device_id
        ) {
          store.dispatch(
            AuthSwitchAccountNewAction({
              deviceId: currentUser?.userSession?.device_id,
              appType: "drive",
            }),
          );
        } else {
          LocalstorageService.removeFromLocalStorage("bigfoot-web");
          const redirectUrl = `${urls.DRIVE_URL}drive/${currentUser?.drive_start_page}`;
          const accountLoginUrl = `${urls.ACCOUNT_URL}?redirect=${encodeURIComponent(
            redirectUrl,
          )}`;

          window.location.href = accountLoginUrl;
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
}
export default axiosClient;
